import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { CourseEditCalendar } from "@shared/models/analytics/course"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course } from "@shared/models/course"
import { findCourseDateCustom } from "cc-frontend/lib/find-course-date"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { assign, partial } from "lodash-es"
import moment from "moment"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultCourseDateActivity from "./default-course-date-activity"
import defaultCourseDateContext from "./default-course-date-context"
import defaultCourseDateModels from "./default-course-date-models"

interface Payload {
  dateString: string
  course: Course
}

export function prepareUnforceOn(payload: Payload): PreparedAction {
  return {
    name: COURSE_DATE_UNFORCE_ON.name,
    payload: payload,
    patches: COURSE_DATE_UNFORCE_ON.patches(payload),
    undoPatches: COURSE_DATE_UNFORCE_ON.undoPatches(payload),
    narrative: partial(COURSE_DATE_UNFORCE_ON.narrative, payload),
  }
}

export const COURSE_DATE_UNFORCE_ON = {
  name: "COURSE_DATE_UNFORCE_ON",

  patches(payload: Payload): Array<PatchModel> {
    // I think we need to recalc the datesOff
    let courseDate = findCourseDateCustom(payload.course, payload.dateString)
    if (courseDate) {
      let patch = Patch.create("course", payload.course.id, "attributes.calendar.dates", {
        id: courseDate.id,
      })
        .set("embedded", "attributes.isForcedOn", false)
        .pull("attributes.calendar.datesForcedOn", payload.dateString)
        .inc("attributes.calendar.version", 1)

      return [patch.patch]
    } else {
      throw Error("CourseDate is undefined!!")
    }
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    let courseDate = findCourseDateCustom(payload.course, payload.dateString)
    if (courseDate) {
      let patch = Patch.create("course", payload.course.id, "attributes.calendar.dates", {
        id: courseDate.id,
      })
        .set("embedded", "attributes.isForcedOn", true)
        .push("attributes.calendar.datesForcedOn", payload.dateString)
        .inc("attributes.calendar.version", 1)

      return [patch.patch]
    } else {
      throw Error("CourseDate is undefined!!")
    }
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
    let models = defaultCourseDateModels(payload, findFn, userId)
    let formattedDateString = moment(payload.dateString).format("ddd, MMM. D, YYYY")
    let courseTitle = payload.course.attributes.title
    let context = defaultCourseDateContext(payload, models)
    let newContext = assign({}, context, { unforcedOn: true })
    let newEvent: CourseEditCalendar = {
      ...createBlankEvent(),
      root_id: models.planbook.id,
      root_type: "planbook",
      date_plan_scheduled_for: payload.dateString,

      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook.id),

      primary_object_type: "course",
      primary_object_id: payload.course.id,
      primary_object_specifier: "calendar",
      primary_object_id_and_ancestor_ids: [payload.course.id, models.planbook.id],

      adverbial_verb: "toggling",

      adverbial_object_type: "lesson_slot",
      adverbial_object_id: payload.dateString,
      adverbial_object_specifier: "off",
      adverbial_object_id_and_ancestor_ids: [payload.course.id, models.planbook.id],
    }

    return {
      title: "Turned class back on",
      titleForAnalytics: "Lesson - Toggle Off",
      description: `Turned your ${courseTitle} class back on for ${formattedDateString}`,
      context: newContext,
      activity: defaultCourseDateActivity(payload, models),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default COURSE_DATE_UNFORCE_ON
